import { differenceInDays, subDays } from 'date-fns';
import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticChartInterval, useUserAllActivityQuery } from '../../../../../services';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, StatsItem, TimeSeriesChart } from '../../helpers';
import { UserProps } from '../User';

import classes from '../../Analytics.module.scss';

const TYPE_LOCALISE_KEY = 'chartTypes.user-all-activity';

export const UserAllActivity: FunctionComponent<UserProps> = memo(({ from, to, platform }) => {
  const { t } = useTranslation();

  const { data: userAllActivity, isFetching: isFetchingUserAllActivity } = useUserAllActivityQuery({
    from,
    to,
    interval: AnalyticChartInterval.AUTO,
    platform,
  });

  const { data: userAllActivityStats, isFetching: isFetchingUserAllActivityStats } =
    useUserAllActivityQuery({ from, to, platform });

  const daysDiff = differenceInDays(new Date(to), new Date(from)) + 1;

  const { data: userAllActivityStatsPrev, isFetching: isFetchingUserAllActivityStatsPrev } =
    useUserAllActivityQuery({
      from: subDays(new Date(from), daysDiff).toISOString(),
      to: subDays(new Date(to), daysDiff).toISOString(),
      platform,
    });

  const [userAllActivityLabels, setUserAllActivityLabels] = useState<string[]>([]);

  useEffect(() => {
    if (userAllActivity && !userAllActivityLabels.length) {
      setUserAllActivityLabels(userAllActivity.labels);
    }
  }, [userAllActivity, userAllActivityLabels.length]);

  const renderStats = useMemo(() => {
    if (isFetchingUserAllActivityStats || isFetchingUserAllActivityStatsPrev) {
      return <Loader stats />;
    }

    if (!userAllActivityStats || !userAllActivityStatsPrev) {
      return null;
    }

    const { datasets } = userAllActivityStats;

    const { datasets: datasetsPrev } = userAllActivityStatsPrev;

    return (
      <div className={classes['analytics__chart-stats']}>
        {datasets.map(({ label, data }, index) => {
          const isDisabled = !userAllActivityLabels.includes(label);
          const isClickable = userAllActivityLabels.length > 1 || isDisabled;

          return (
            <StatsItem
              key={label}
              label={label}
              localiseKey={`userAllActivityLabels.${label}`}
              hintLocaliseKey={`userAllActivityHints.${label}`}
              color={COLORS[index]}
              value={data[0] ?? 0}
              valuePrev={datasetsPrev.find((set) => set.label === label)?.data[0] ?? 0}
              labels={userAllActivityLabels}
              setLabels={setUserAllActivityLabels}
              isDisabled={isDisabled}
              isClickable={isClickable}
            />
          );
        })}
      </div>
    );
  }, [
    isFetchingUserAllActivityStats,
    isFetchingUserAllActivityStatsPrev,
    userAllActivityLabels,
    userAllActivityStats,
    userAllActivityStatsPrev,
  ]);

  return (
    <ChartWrapper typeLocaliseKey={TYPE_LOCALISE_KEY} infoLocaliseKey={'userAllActivity.info'}>
      {renderStats}
      {userAllActivity && (
        <TimeSeriesChart
          timeSeries={userAllActivity}
          labels={userAllActivityLabels}
          localiseKeyLabels={'userAllActivityLabels'}
          loading={isFetchingUserAllActivity}
          ariaLabel={t(TYPE_LOCALISE_KEY)}
        />
      )}
    </ChartWrapper>
  );
});
